import { useMemo, useState } from "react";

import {
  dailyRecipes,
  offerByDirection,
  reflectionCards,
  siteCopy,
  type DailyGift,
  type DailyRecipe,
  type ReflectionCard,
} from "../../content";
import {
  DEFAULT_DAILY_TIME_ZONE,
  drawDaily,
} from "../../lib/daily-draw";

import "./daily-gift.css";

const copy = siteCopy.dailyGift;

export type DailyGiftKind = "card" | "recipe";

export interface DailyGiftPanelProps {
  kind?: DailyGiftKind;
  date?: Date;
  timeZone?: string;
  onClose?: () => void;
  onTakeAway?: (gift: DailyGift) => void;
  embedded?: boolean;
}

function formatDay(dayKey: string): string {
  return new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${dayKey}T12:00:00Z`));
}

function ReflectionCardView({ card, embedded }: { card: ReflectionCard; embedded: boolean }) {
  const GiftTitle = embedded ? "h4" : "h3";
  const DetailTitle = embedded ? "h5" : "h4";
  return (
    <article className="daily-gift__card">
      <p className="daily-gift__theme">Kaart · {card.theme}</p>
      <GiftTitle className="daily-gift__gift-title">{card.title}</GiftTitle>
      <p className="daily-gift__message">{card.message}</p>
      <div className="daily-gift__question">
        <span>{copy.cardPromptLabel}</span>
        <p>{card.prompt}</p>
      </div>
      <div className="daily-gift__ritual">
        <span aria-hidden="true">≈</span>
        <div>
          <DetailTitle>{copy.ritualTitle}</DetailTitle>
          <p>{card.ritual}</p>
        </div>
      </div>
    </article>
  );
}

function RecipeView({ recipe, embedded }: { recipe: DailyRecipe; embedded: boolean }) {
  const GiftTitle = embedded ? "h4" : "h3";
  const DetailTitle = embedded ? "h5" : "h4";
  return (
    <article className="daily-gift__recipe">
      <p className="daily-gift__theme">Recept · {recipe.servings}</p>
      <GiftTitle className="daily-gift__gift-title">{recipe.title}</GiftTitle>
      <p className="daily-gift__message">{recipe.intro}</p>

      <div className="daily-gift__recipe-grid">
        <section aria-labelledby={`${recipe.id}-ingredients`}>
          <DetailTitle id={`${recipe.id}-ingredients`}>{copy.ingredientsTitle}</DetailTitle>
          <ul>
            {recipe.ingredients.map((ingredient) => (
              <li key={ingredient}>{ingredient}</li>
            ))}
          </ul>
        </section>
        <section aria-labelledby={`${recipe.id}-steps`}>
          <DetailTitle id={`${recipe.id}-steps`}>{copy.stepsTitle}</DetailTitle>
          <ol>
            {recipe.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      </div>

      <p className="daily-gift__labels">
        <span className="daily-gift__visually-hidden">Kenmerken: </span>
        {recipe.dietaryLabels.join(" · ")}
      </p>
      {recipe.note ? <p className="daily-gift__note">Let op: {recipe.note}</p> : null}
      <div className="daily-gift__question">
        <span>{copy.recipeReflectionLabel}</span>
        <p>{recipe.reflection}</p>
      </div>
    </article>
  );
}

export function DailyGiftPanel({
  kind = "card",
  date,
  timeZone = DEFAULT_DAILY_TIME_ZONE,
  onClose,
  onTakeAway,
  embedded = false,
}: DailyGiftPanelProps) {
  const [openedAt] = useState(() => new Date());
  const effectiveDate = date ?? openedAt;
  const draw = useMemo(
    () =>
      kind === "card"
        ? drawDaily(reflectionCards, {
            channel: "soul-shack-reflection-card-v1",
            date: effectiveDate,
            timeZone,
          })
        : drawDaily(dailyRecipes, {
            channel: "soul-shack-recipe-v1",
            date: effectiveDate,
            timeZone,
          }),
    [effectiveDate, kind, timeZone],
  );
  const special = offerByDirection.special;
  const PanelTitle = embedded ? "h3" : "h2";
  const FooterTitle = embedded ? "h4" : "h3";

  return (
    <section
      className="daily-gift"
      data-embedded={embedded || undefined}
      aria-labelledby="daily-gift-title"
    >
      <header className="daily-gift__header">
        <div>
          <p className="daily-gift__eyebrow">{copy.eyebrow}</p>
          <PanelTitle className="daily-gift__title" id="daily-gift-title">
            {kind === "card" ? copy.cardTitle : copy.recipeTitle}
          </PanelTitle>
          <p className="daily-gift__date">{formatDay(draw.dayKey)}</p>
        </div>
        {onClose && !embedded ? (
          <button className="daily-gift__quiet-button" type="button" onClick={onClose}>
            Sluiten
          </button>
        ) : null}
      </header>

      <p className="daily-gift__daily-note">
        {kind === "card" ? copy.cardDailyNote : copy.recipeDailyNote}
      </p>

      {draw.item.kind === "card" ? (
        <ReflectionCardView card={draw.item} embedded={embedded} />
      ) : (
        <RecipeView recipe={draw.item} embedded={embedded} />
      )}

      <footer className="daily-gift__footer">
        <div>
          <FooterTitle>{copy.footerTitle}</FooterTitle>
          <p>{copy.footerBody}</p>
        </div>
        <div className="daily-gift__actions">
          {onTakeAway ? (
            <button
              className="daily-gift__secondary-button"
              type="button"
              onClick={() => onTakeAway(draw.item)}
            >
              Neem dit mee
            </button>
          ) : null}
          <a className="daily-gift__primary-link" href={special.href} target="_blank" rel="noreferrer">
            {special.ctaLabel}
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </footer>
    </section>
  );
}

export function DailyCardPanel(props: Omit<DailyGiftPanelProps, "kind">) {
  return <DailyGiftPanel {...props} kind="card" />;
}

export function DailyRecipePanel(props: Omit<DailyGiftPanelProps, "kind">) {
  return <DailyGiftPanel {...props} kind="recipe" />;
}
